import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Globe, Eye, EyeOff, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const CategoryForm = ({ viewOnly = false }) => {
  const { t, lang } = useTranslation();
  const { notify } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    default_name: '',
    parent_id: null,
    labels: { en: '', zh: '' },
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTranslations, setShowTranslations] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [canEditTranslations, setCanEditTranslations] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-open translations when user can edit them on a system category
  useEffect(() => {
    if (canEditTranslations) {
      setShowTranslations(true);
    }
  }, [canEditTranslations]);

  // Parent selector state
  const [parentOpen, setParentOpen] = useState(false);
  const [parentSearch, setParentSearch] = useState('');
  const [parentExpanded, setParentExpanded] = useState({});
  const parentRef = useRef(null);

  const locales = [
    { code: 'zh', label: t('categories.chinese_label') },
    { code: 'en', label: t('categories.english_label') },
  ];

  const getLocalizedName = (cat) => {
    if (lang && cat.labels && cat.labels[lang]) return cat.labels[lang];
    return cat.default_name || '';
  };

  // Load categories for parent selection
  useEffect(() => {
    fetch('/api/categories')
      .then(res => {
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        return res.json();
      })
      .then(res => {
        if (res.status === 'success' && res.data) {
          setCategories(res.data);
        } else {
          setCategories([]);
        }
      })
      .catch(err => {
        console.error('Failed to fetch categories:', err);
        setCategories([]);
      });
  }, []);

  // Load category data if editing
  useEffect(() => {
    if (id) {
      fetch(`/api/categories/${id}`)
        .then(res => {
          if (!res.ok) throw new Error(`API returned ${res.status}`);
          return res.json();
        })
        .then(res => {
          if (res.status === 'success' && res.data) {
            const cat = res.data;
            setFormData({
              default_name: cat.default_name || '',
              parent_id: cat.parent_id || null,
              labels: {
                en: (cat.labels && cat.labels.en) || '',
                zh: (cat.labels && cat.labels.zh) || '',
              },
            });
            if (cat.is_system == 1) {
              setIsSystem(true);
              if (cat.can_edit_translations) {
                setCanEditTranslations(true);
              }
            }
          }
        })
        .catch(err => console.error('Failed to fetch category detail:', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id]);

  // Close parent dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (parentRef.current && !parentRef.current.contains(e.target)) {
        setParentOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (effectiveViewOnly) {
      navigate('/categories');
      return;
    }
    const endpoint = id ? `/api/categories/${id}/edit` : '/api/categories/add';
    const method = id ? 'PUT' : 'POST';

    setIsSubmitting(true);
    try {
      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.success'), 'success');
        navigate('/categories');
      } else {
        notify(t('common.error') + ': ' + data.message, 'error');
      }
    } catch (err) {
      notify(t('common.error'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateLabel = (locale, value) => {
    setFormData(prev => ({
      ...prev,
      labels: { ...prev.labels, [locale]: value },
    }));
  };

  const selectedParent = categories.find(c => c.id === formData.parent_id);

  // Build ancestor path for a matching category
  const getPath = (catId, result = []) => {
    const cat = categories.find(c => c.id === catId);
    if (!cat) return result;
    result.unshift(cat);
    if (cat.parent_id) return getPath(cat.parent_id, result);
    return result;
  };

  // Check if category matches search
  const matchesSearch = (cat) => {
    if (!parentSearch || !parentSearch.trim()) return true;
    const q = parentSearch.toLowerCase();
    const def = (cat.default_name || '').toLowerCase();
    const zh = ((cat.labels && cat.labels.zh) || '').toLowerCase();
    const en = ((cat.labels && cat.labels.en) || '').toLowerCase();
    return def.includes(q) || zh.includes(q) || en.includes(q);
  };

  // Get matching IDs (including ancestors for path)
  const getMatchingIds = () => {
    if (!parentSearch || !parentSearch.trim()) return null;
    const matching = new Set();
    categories.forEach(c => {
      if (c.id !== (id ? parseInt(id) : 0) && matchesSearch(c)) {
        matching.add(c.id);
        let parentId = c.parent_id;
        while (parentId) {
          matching.add(parentId);
          const p = categories.find(x => x.id === parentId);
          parentId = p ? p.parent_id : null;
        }
      }
    });
    return matching;
  };

  const parentMatchingIds = getMatchingIds();
  const isParentSearching = parentSearch && parentSearch.trim();

  // Build filtered tree for parent selector
  const renderTreeOptions = (parentId = null, level = 0) => {
    // Search mode: show all matching categories flat, bypass parent hierarchy
    if (isParentSearching) {
      return categories
        .filter(c => c.id !== (id ? parseInt(id) : 0) && matchesSearch(c))
        .map(c => {
          const hasChildren = categories.some(ch => ch.parent_id === c.id);
          const path = getPath(c.id);
          return (
            <div key={c.id}>
              <div
                className={`flex items-center p-2 cursor-pointer text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${
                  formData.parent_id === c.id ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-medium' : ''
                }`}
                onClick={() => {
                  setFormData(prev => ({ ...prev, parent_id: c.id }));
                  setParentOpen(false);
                  setParentSearch('');
                }}
              >
                <span className="text-slate-400 dark:text-slate-500 mr-1 shrink-0">{hasChildren ? '▾' : '•'}</span>
                <span className="truncate">
                  {path.map((p, i) => (
                    <span key={p.id}>
                      {i > 0 && <span className="text-slate-300 dark:text-slate-600 mx-1">›</span>}
                      <span className={p.id === c.id ? 'font-medium' : 'text-slate-500 dark:text-slate-400'}>
                        {getLocalizedName(p)}
                      </span>
                    </span>
                  ))}
                </span>
              </div>
            </div>
          );
        });
    }

    // Normal tree mode: hierarchy-based, with expand/collapse
    return categories
      .filter(c => c.parent_id === parentId && c.id !== (id ? parseInt(id) : 0))
      .map(c => {
        const hasChildren = categories.some(ch => ch.parent_id === c.id);
        const isExpanded = parentExpanded[c.id] || false;

        return (
          <div key={c.id}>
            <div
              className={`flex items-center p-2 cursor-pointer text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${
                formData.parent_id === c.id ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-medium' : ''
              }`}
              style={{ paddingLeft: `${level * 14 + 8}px` }}
            >
              <span
                className="shrink-0 w-5 flex items-center justify-center text-slate-400 mr-1 hover:text-slate-600"
                onClick={e => { e.stopPropagation(); hasChildren && setParentExpanded(prev => ({...prev, [c.id]: !prev[c.id]})); }}
              >
                {hasChildren ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="text-xs">•</span>}
              </span>
              <span
                className={`truncate ${formData.parent_id === c.id ? 'font-bold' : ''}`}
                onClick={() => {
                  setFormData(prev => ({ ...prev, parent_id: c.id }));
                  setParentOpen(false);
                  setParentSearch('');
                }}
              >
                {getLocalizedName(c)}
              </span>
            </div>
            {isExpanded && renderTreeOptions(c.id, level + 1)}
          </div>
        );
      });
  };

  // System categories are view-only, unless super admin who can edit translations
  const effectiveViewOnly = viewOnly || (isSystem && !canEditTranslations);

  if (loading) return <div className="text-center p-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-3">
          {viewOnly ? t('categories.view_category') : (id ? t('categories.edit_category') : t('categories.new_category'))}
          {isSystem && (
            <span className="px-2 py-0.5 text-xs font-bold uppercase rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700">
              SYSTEM
            </span>
          )}
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/categories')} className="p-2 hover:bg-slate-200 rounded-full">
          <X size={20} />
        </button>
      </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-8">
        {/* System category protected wrapper: blocks all non-translation fields */}
        <div className={isSystem ? 'pointer-events-none select-none' : ''}>
        
        {/* Default name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('categories.default_name')} <span className="text-red-500">*</span>
          </label>
          <input 
            className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent"
            value={formData.default_name}
            onChange={e => setFormData(prev => ({...prev, default_name: e.target.value}))}
            required
            placeholder={t('categories.default_name')}
            disabled={effectiveViewOnly}
          />
        </div>

        {/* Parent category - searchable tree selector */}
        <div className="space-y-2" ref={parentRef}>
          <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {t('categories.parent')}
          </label>
          <div className="relative">
            <div
              className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg cursor-pointer flex justify-between items-center bg-white dark:bg-slate-800"
              onClick={() => setParentOpen(!parentOpen)}
            >
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {selectedParent ? getLocalizedName(selectedParent) : t('categories.root_category')}
              </span>
              <ChevronDown size={16} className="text-slate-400 dark:text-slate-500" />
            </div>
            {parentOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden">
                <div className="p-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                    <input
                      className="w-full pl-8 pr-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded outline-none focus:ring-1 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
                      placeholder={t('categories.search_placeholder')}
                      value={parentSearch}
                      onChange={e => setParentSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto p-1">
                  {/* Root option - hidden during search to reduce noise */}
                  {!isParentSearching && (
                    <div
                      className={`flex items-center p-2 cursor-pointer text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${
                        !formData.parent_id ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-medium' : ''
                      }`}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, parent_id: null }));
                        setParentOpen(false);
                        setParentSearch('');
                      }}
                    >
                      <span className="w-5 flex items-center justify-center text-xs text-slate-400 mr-1">⊘</span>
                      {t('categories.root_category')}
                    </div>
                  )}
                  {renderTreeOptions()}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>  {/* End system-protected fields */}

        {/* Translations toggle */}
        <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => setShowTranslations(!showTranslations)}
              className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
            >
              <Globe size={16} />
              <span>{t('categories.translations')}</span>
              {showTranslations ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {showTranslations && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
              {locales.map(locale => (
                <div key={locale.code} className="space-y-1">
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    {locale.label}
                  </label>
                  <input 
                    className="w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-transparent text-sm"
                    value={formData.labels[locale.code] || ''}
                    onChange={e => updateLabel(locale.code, e.target.value)}
                    placeholder={`${locale.label} name`}
                    disabled={canEditTranslations ? false : effectiveViewOnly}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t">
          {effectiveViewOnly ? (
            <button 
              type="button" 
              onClick={() => navigate('/categories')} 
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common.close')}
            </button>
          ) : (
            <>
              <button 
                type="button" 
                onClick={() => navigate('/categories')} 
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button 
                type="submit" 
                disabled={isSubmitting}
                className={`px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('common.processing')}
                  </span>
                ) : (
                  <><Save size={18} />{id ? t('common.update') : t('common.add')}</>
                )}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;
